import React from 'react';
import { Accordion, Checkbox, Text, Anchor, Group } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { CourseModule, ModuleProgressItem } from '../../types';
import { downloadModuleFile } from '../../hooks/useCourseApi';
import { showToast } from '../../utils/toast';

function isAllowedVideoUrl(url: string): boolean {
  try {
    const parsed = new URL(url);
    return (
      parsed.hostname === 'www.youtube.com' ||
      parsed.hostname === 'youtube.com' ||
      parsed.hostname === 'player.vimeo.com' ||
      parsed.hostname === 'www.youtube-nocookie.com'
    );
  } catch {
    return false;
  }
}

interface ModuleListProps {
  courseId: string;
  modules: CourseModule[];
  moduleProgress: ModuleProgressItem[];
  onModuleComplete: (moduleId: string, isCompleted: boolean) => void;
  isDisabled: boolean;
}

export const ModuleList: React.FC<ModuleListProps> = ({
  courseId,
  modules,
  moduleProgress,
  onModuleComplete,
  isDisabled,
}) => {
  const { t } = useTranslation();

  const sortedModules = [...modules].sort((a, b) => a.order - b.order);

  const progressMap = React.useMemo(() => {
    const map = new Map<string, boolean>();
    moduleProgress.forEach((mp) => {
      map.set(mp.moduleId, mp.isCompleted);
    });
    return map;
  }, [moduleProgress]);

  return (
    <Accordion variant="separated">
      {sortedModules.map((mod) => {
        const isCompleted = progressMap.get(mod._id) ?? false;

        return (
          <Accordion.Item key={mod._id} value={mod._id}>
            <Accordion.Control>
              <Group justify="space-between" wrap="nowrap" gap="sm">
                <Text fw={500}>{mod.title}</Text>
                <Checkbox
                  label={t('learning.moduleDone')}
                  checked={isCompleted}
                  disabled={isDisabled}
                  onClick={(e) => e.stopPropagation()}
                  onChange={(e) => {
                    e.stopPropagation();
                    onModuleComplete(mod._id, e.currentTarget.checked);
                  }}
                  size="sm"
                />
              </Group>
            </Accordion.Control>
            <Accordion.Panel>
              {mod.contentType === 'text' && (
                /* Safe: React auto-escapes text content. If markdown rendering is added,
                   sanitize with DOMPurify to prevent XSS. */
                <div style={{ whiteSpace: 'pre-wrap' }}>{mod.textContent}</div>
              )}

              {mod.contentType === 'video' && mod.videoUrl && isAllowedVideoUrl(mod.videoUrl) ? (
                <div
                  style={{
                    position: 'relative',
                    paddingBottom: '56.25%',
                    height: 0,
                    overflow: 'hidden',
                    width: '100%',
                  }}
                >
                  <iframe
                    src={mod.videoUrl}
                    title={mod.title}
                    sandbox="allow-scripts allow-same-origin"
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      border: 0,
                    }}
                    allowFullScreen
                  />
                </div>
              ) : mod.contentType === 'video' && mod.videoUrl ? (
                <Anchor href={mod.videoUrl} target="_blank">
                  {mod.videoUrl}
                </Anchor>
              ) : null}

              {mod.contentType === 'file' && mod.fileUrl && (
                <Anchor
                  component="button"
                  type="button"
                  onClick={() =>
                    downloadModuleFile(courseId, mod._id, mod.fileName).catch(() =>
                      showToast({ title: t('common.error'), message: '', color: 'red' }),
                    )
                  }
                >
                  {mod.fileName || t('learning.file')}
                </Anchor>
              )}
            </Accordion.Panel>
          </Accordion.Item>
        );
      })}
    </Accordion>
  );
};
